/**
 * A trend in the space of a word.
 *
 * No axes, no gridlines, no labels — a sparkline is read as a shape beside a
 * number, not decoded. The number it sits next to carries the value; this
 * carries only the direction, which is the one thing a single figure cannot
 * say. Fewer than two points draws nothing rather than a misleading flat line.
 */
export function Sparkline({
  points,
  color = 'var(--accent)',
  width = 90,
  height = 22,
}: {
  points: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (points.length < 2) {
    return <span className="muted" style={{ fontSize: 11 }}>—</span>;
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1);
  // 1.5px inset top and bottom so the stroke is never clipped at the extremes.
  const y = (v: number) => height - 1.5 - ((v - min) / span) * (height - 3);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(p).toFixed(1)}`).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(points.length - 1) * step} cy={y(points[points.length - 1]!)} r={2.2} fill={color} />
    </svg>
  );
}
